import React, { useState } from 'react';
import { reportAPI } from '../services/api';
import toast from 'react-hot-toast';
import { BarChart2, Download, FileText, Users, MapPin, MessageSquare, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import useDarkReady from '../theme/useDarkReady';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function ReportCard({ icon: Icon, title, description, color, onGenerate, loading }) {
  return (
    <div className="card" style={{ cursor: 'pointer' }} onClick={onGenerate}>
      <div className="card-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 44, height: 44, background: `${color}20`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={20} color={color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '.9rem', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{description}</div>
        </div>
        <button className="btn btn-outline btn-sm" disabled={loading}>
          {loading ? '...' : <Download size={13} />}
        </button>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  useDarkReady();
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState('');
  const [params, setParams] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const generate = async (type) => {
    setLoading(type);
    setActiveReport(type);
    setReportData(null);
    try {
      let res;
      if (type === 'daily') res = await reportAPI.dailyCollection({ date: params.date });
      else if (type === 'monthly') res = await reportAPI.monthlyCollection({ month: params.month, year: params.year });
      else if (type === 'due') res = await reportAPI.dueCustomers();
      else if (type === 'area') res = await reportAPI.areaWise();
      else if (type === 'complaints') res = await reportAPI.complaints({});
      else if (type === 'renewal') res = await reportAPI.renewal({ month: params.month, year: params.year });
      setReportData(res.data.data);
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(''); }
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>Reports</h2>
      <p className="text-muted text-small mb-6">Generate and view business reports</p>

      {/* Report filters */}
      <div className="card mb-6">
        <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label className="form-label text-small">Date (for daily)</label>
            <input type="date" className="form-control" style={{ width: 160 }} value={params.date} onChange={e => setParams(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <label className="form-label text-small">Month</label>
            <select className="form-control" style={{ width: 120 }} value={params.month} onChange={e => setParams(p => ({ ...p, month: e.target.value }))}>
              {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label text-small">Year</label>
            <select className="form-control" style={{ width: 100 }} value={params.year} onChange={e => setParams(p => ({ ...p, year: e.target.value }))}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Report buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, marginBottom: 28 }}>
        <ReportCard icon={FileText} title="Daily Collection Report" description="All payments collected on a specific date" color="#059669" onGenerate={() => generate('daily')} loading={loading === 'daily'} />
        <ReportCard icon={BarChart2} title="Monthly Collection Report" description="Revenue breakdown for a month" color="var(--primary)" onGenerate={() => generate('monthly')} loading={loading === 'monthly'} />
        <ReportCard icon={Users} title="Due Customers Report" description="Customers with outstanding balances" color="#dc2626" onGenerate={() => generate('due')} loading={loading === 'due'} />
        <ReportCard icon={MapPin} title="Area-Wise Customer Report" description="Customer distribution by area" color="var(--primary)" onGenerate={() => generate('area')} loading={loading === 'area'} />
        <ReportCard icon={MessageSquare} title="Complaint Report" description="Support tickets and resolution stats" color="#d97706" onGenerate={() => generate('complaints')} loading={loading === 'complaints'} />
        <ReportCard icon={RefreshCw} title="Renewal Report" description="Renewals processed in a month" color="#0891b2" onGenerate={() => generate('renewal')} loading={loading === 'renewal'} />
      </div>

      {/* Report Output */}
      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}

      {reportData && activeReport === 'daily' && (
        <div className="card">
          <div className="card-header">
            <h3>Daily Collection — {reportData.date}</h3>
            <span style={{ fontWeight: 700, color: 'var(--success)' }}>Total: ₹{fmt(reportData.totalAmount)}</span>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Receipt</th><th>Customer</th><th>For</th><th>Method</th><th>Amount</th><th>By</th></tr></thead>
              <tbody>
                {reportData.payments?.map(p => (
                  <tr key={p._id}>
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{p.receiptNumber}</td>
                    <td><div style={{ fontWeight: 500 }}>{p.customer?.name}</div><div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div></td>
                    <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{p.paymentFor?.replace(/_/g, ' ')}</td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod}</span></td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                    <td style={{ fontSize: '.78rem' }}>{p.collectedBy?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Method breakdown */}
          <div style={{ padding: 16, display: 'flex', gap: 12, flexWrap: 'wrap', borderTop: '1px solid var(--border)' }}>
            {reportData.summary?.map(s => (
              <div key={s._id} style={{ background: 'var(--bg)', padding: '8px 14px', borderRadius: 8, fontSize: '.82rem' }}>
                <span style={{ textTransform: 'capitalize', color: 'var(--text-muted)' }}>{s._id?.replace('_', ' ')}:</span>
                <strong style={{ marginLeft: 6 }}>₹{fmt(s.total)}</strong>
                <span style={{ color: 'var(--text-light)', marginLeft: 4 }}>({s.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {reportData && activeReport === 'monthly' && (
        <div className="card">
          <div className="card-header">
            <h3>Monthly Collection — {months[reportData.month - 1]} {reportData.year}</h3>
            <span style={{ fontWeight: 700, color: 'var(--success)' }}>₹{fmt(reportData.totalAmount)}</span>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={reportData.dailyBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
                <XAxis dataKey="_id" tick={{ fontSize: 11 }} label={{ value: 'Day', position: 'insideBottom', offset: -2 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`₹${fmt(v)}`, 'Collection']} />
                <Bar dataKey="total" fill="var(--primary)" radius={[3, 3, 0, 0]} name="Daily Collection" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ padding: '0 16px 16px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {reportData.methodBreakdown?.map(m => (
              <div key={m._id} style={{ background: 'var(--bg)', padding: '8px 14px', borderRadius: 8, fontSize: '.82rem' }}>
                <span style={{ textTransform: 'capitalize', color: 'var(--text-muted)' }}>{m._id?.replace('_', ' ')}:</span>
                <strong style={{ marginLeft: 6 }}>₹{fmt(m.total)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {reportData && activeReport === 'due' && (
        <div className="card">
          <div className="card-header">
            <h3>Due Customers</h3>
            <span style={{ fontWeight: 700, color: 'var(--danger)' }}>Total Outstanding: ₹{fmt(reportData.totalOutstanding)}</span>
          </div>
          <div className="table-container">
            <table>
              <thead><tr><th>Customer</th><th>Mobile</th><th>Area</th><th>Plan</th><th>Outstanding</th></tr></thead>
              <tbody>
                {reportData.customers?.map(c => (
                  <tr key={c._id}>
                    <td><div style={{ fontWeight: 500 }}>{c.name}</div><div style={{ fontSize: '.72rem' }}>{c.customerId}</div></td>
                    <td style={{ fontSize: '.85rem' }}>{c.mobile}</td>
                    <td style={{ fontSize: '.82rem' }}>{c.address?.area}</td>
                    <td style={{ fontSize: '.82rem' }}>{c.broadband?.plan?.name || c.catv?.package?.name || '—'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--danger)' }}>₹{fmt(c.outstandingBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportData && activeReport === 'area' && (
        <div className="card">
          <div className="card-header"><h3>Area-Wise Customer Distribution</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={reportData.areaStats || reportData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="_id" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" fill="var(--primary)" name="Total" radius={[0, 3, 3, 0]} />
                <Bar dataKey="active" fill="var(--cl-059669)" name="Active" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
